/**
 * useSellerReviewQueue — keeps the pending-seller-verification count alive.
 *
 * Two hooks:
 *   useSellerReviewQueueSync({ user, socket, activeCountry })
 *     The OWNER. Mounted once, in AdminNavbar. For staff who may review
 *     sellers it (1) loads GET /seller/review-queue on mount, on window focus,
 *     on tab visibility and on a slow interval, and (2) joins the gated
 *     `sellerReview:<country>` socket room on the navbar's existing socket so
 *     every change the server re-counts arrives as a `seller:reviewQueue`
 *     frame. Non-reviewers get an empty store and no requests.
 *
 *   useSellerReviewQueue()
 *     Read-only subscription for anything else that shows the count (sidebar
 *     chip, Sellers page). Never fetches.
 *
 * Persistence comes from the server, not the browser: the count is read from
 * the database on every load, so it survives reloads, new tabs and devices,
 * and it cannot be "cleared" in the UI while sellers are still waiting.
 */

import { useCallback, useEffect, useState } from "react";
import { apiGetSellerReviewQueue } from "../api";
import {
  SELLER_REVIEW_EVENT,
  applySellerReviewEvent,
  canReviewSellers,
  getSellerReviewQueue,
  resetSellerReviewQueue,
  setSellerReviewQueue,
  subscribeSellerReviewQueue,
} from "../utilities/sellerReviewQueue";

/** Fallback poll so a long-lived tab that missed a frame still converges. */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export function useSellerReviewQueue() {
  const [queue, setQueue] = useState(getSellerReviewQueue);
  useEffect(() => subscribeSellerReviewQueue(setQueue), []);
  return queue;
}

export function useSellerReviewQueueSync({ user, socket, activeCountry } = {}) {
  const allowed = canReviewSellers(user?.result);

  const refresh = useCallback(async () => {
    if (!allowed) return;
    try {
      const { data } = await apiGetSellerReviewQueue();
      setSellerReviewQueue(data);
    } catch (_error) {
      // Keep the last known snapshot; the socket frame or the next focus
      // will correct it. A failed count must never blank the bell.
    }
  }, [allowed]);

  // HTTP snapshot: mount, focus, visibility, slow interval.
  useEffect(() => {
    if (!allowed) {
      resetSellerReviewQueue();
      return undefined;
    }
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [allowed, refresh, activeCountry]);

  // Live frames on the shared navbar socket.
  useEffect(() => {
    if (!allowed || !socket) return undefined;

    const join = () => {
      try {
        socket.emit("joinSellerReviewRoom", activeCountry, (ack) => {
          if (ack?.ok && ack.pending !== null && ack.pending !== undefined) {
            setSellerReviewQueue(ack);
          }
        });
      } catch (_error) {
        /* the HTTP snapshot still covers us */
      }
    };
    const onFrame = (payload) => applySellerReviewEvent(payload);

    socket.on(SELLER_REVIEW_EVENT, onFrame);
    socket.on("connect", join);
    if (socket.connected) join();

    return () => {
      socket.off(SELLER_REVIEW_EVENT, onFrame);
      socket.off("connect", join);
      try {
        if (socket.connected) socket.emit("leaveSellerReviewRoom");
      } catch (_error) {
        /* socket is going away anyway */
      }
    };
  }, [allowed, socket, activeCountry]);

  return useSellerReviewQueue();
}

export default useSellerReviewQueue;
